import styles from "./StockInfo.module.scss";
import { FC } from "react";
import { Stock } from "@/models/stock";
import { classNames, nFormatter } from "@/lib/utils";

import { useAtomValue } from "jotai";
import { textColorState, themeState } from "@/lib/store";
import { Box, Chip, Typography } from "@mui/material";

const MOBILE = "@media (max-width: 768px)";
const WIDE = "@media (min-width: 1920px)";

const StockInfoDetails: FC<{ stock: Stock; isCustomView?: boolean }> = ({ stock, isCustomView = false }) => {
    const theme = useAtomValue(themeState);
    const textColor = useAtomValue(textColorState);

    const mktCapRank = stock?.mkt_cap_rank;
    const mktCapUsd = stock?.mkt_cap_usd;
    const mktCapCountry = stock?.mkt_cap_country_iso;
    // Widths are media queries, not useMediaQuery: the server cannot match one, so phones were
    // served the one-row layout and re-laid it out as two rows after start-up (a layout shift).

    return (
        <Box className={classNames(styles[theme], styles.StockInfo, isCustomView && styles["custom-view"])} sx={{ gridArea: "b" }}>
            <>
                <Box sx={{ width: "100%", justifyContent: "space-between", textAlign: "center", flexWrap: "nowrap", [MOBILE]: { flexWrap: "wrap" }, height: "100%", overflow: "auto" }} className={styles["box-container"]}>
                    {[
                        {
                            subtitle: "Market Cap",
                            info: `${nFormatter(mktCapUsd, "auto")} USD ${mktCapRank !== null ? `| # ${mktCapRank} in ${mktCapCountry}` : ""}`,
                        },
                        {
                            subtitle: "Volume",
                            info: `${nFormatter(stock?.volume_3m_this_exchange, 2)} shares | ${nFormatter(stock?.volume_3m_usd, 2)} USD`,
                        },
                        {
                            subtitle: "Shares Outstanding",
                            info: `${nFormatter(stock?.outstanding_shares, 2)}`,
                        },
                        {
                            subtitle: "Free Float Shares",
                            info: stock?.free_floating_shares ? `${stock?.free_floating_shares.toFixed(2).trimEnd()}% | ${nFormatter((stock?.free_floating_shares * stock?.outstanding_shares) / 100, 2)}` : "N/A",
                        },
                    ].map((data, index) => {
                        return (
                            <Box className={styles["info-container"]} key={index} sx={{ width: "100%", [MOBILE]: { width: "50%" } }}>
                                {/* A label for the value under it, not a section heading. */}
                                <Typography variant="subtitle2" component="p" sx={{ fontWeight: "bold", color: textColor }}>
                                    {data.subtitle}
                                </Typography>

                                <Chip size="small" label={<Typography variant="body2">{data.info}</Typography>} variant="outlined" sx={{ height: 14, [MOBILE]: { height: 24 }, [WIDE]: { height: 24 } }} />
                            </Box>
                        );
                    })}
                </Box>
            </>
        </Box>
    );
};

export default StockInfoDetails;
